import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import { cellClass, CustomerFilter, DateField, EmptyRow, FilterGrid, RangeError, ReportCard, ReportPage, ReportStatus, ReportTable, SummaryTiles, useCustomerOptions, useReportData, useReportParams } from './ReportParts';
import { dateParam, dateRangeError, enumLabel, reportMoney } from './reportUi';

const ITEM_COLUMNS = [{ label: 'Item type' }, { label: 'Lines', numeric: true }, { label: 'Net revenue', numeric: true }, { label: 'Discounts', numeric: true }, { label: 'Tax', numeric: true }];
const CUSTOMER_COLUMNS = [{ label: 'Customer' }, { label: 'Invoices', numeric: true }, { label: 'Net revenue', numeric: true }, { label: 'Discounts', numeric: true }, { label: 'Tax', numeric: true }];

export function RevenueReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const customerOptions = useCustomerOptions(api);
  const { params, change } = useReportParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const customerId = params.get('customerId') || '';
  const rangeError = dateRangeError(startDate, endDate);
  const query = { startDate, endDate, customerId };
  const state = useReportData(
    rangeError ? null : JSON.stringify(query),
    () => api.getRevenueReport(query),
    'Could not load revenue',
  );

  return (
    <ReportPage
      title="Revenue"
      intro="Invoiced revenue and tax by invoice date, computed from invoice line items (not the GL revenue accounts). Revenue is net of discounts; the discounts are shown separately. POSTED, PARTIAL and PAID invoices count; DRAFT and VOID are excluded, so a cancelled invoice drops out of its original period."
    >
      <FilterGrid>
        <DateField id="revenue-start" label="Start date" value={startDate} onChange={(value) => change({ startDate: value })} />
        <DateField id="revenue-end" label="End date" value={endDate} onChange={(value) => change({ endDate: value })} />
        <CustomerFilter id="revenue-customer" value={customerId} onChange={(value) => change({ customerId: value })} options={customerOptions} />
      </FilterGrid>
      <RangeError message={rangeError} />
      <ReportStatus state={state} label="revenue">
        {(report) => (
          <>
            <SummaryTiles tiles={[
              { label: 'Net revenue', value: reportMoney(report.totalRevenue) },
              { label: 'Discounts', value: reportMoney(report.totalDiscounts ?? '0') },
              { label: 'Total tax', value: reportMoney(report.totalTax) },
            ]} />
            <ReportCard title="By item type">
              <ReportTable columns={ITEM_COLUMNS} caption="Revenue by item type">
                {report.byItemType.length === 0 ? <EmptyRow colSpan={ITEM_COLUMNS.length} message="No invoiced revenue in this period." /> : report.byItemType.map((row) => (
                  <tr key={row.itemType}>
                    <td className={cellClass()} data-label="Item type" data-primary>{enumLabel(row.itemType)}</td>
                    <td className={cellClass(true)} data-label="Lines">{row.lineCount}</td>
                    <td className={cellClass(true)} data-label="Net revenue">{reportMoney(row.revenue)}</td>
                    <td className={cellClass(true)} data-label="Discounts">{reportMoney(row.discounts ?? '0')}</td>
                    <td className={cellClass(true)} data-label="Tax">{reportMoney(row.tax)}</td>
                  </tr>
                ))}
              </ReportTable>
            </ReportCard>
            <ReportCard title="By customer">
              <ReportTable columns={CUSTOMER_COLUMNS} caption="Revenue by customer">
                {report.byCustomer.length === 0 ? <EmptyRow colSpan={CUSTOMER_COLUMNS.length} message="No invoiced revenue in this period." /> : report.byCustomer.map((row) => (
                  <tr key={row.customerId}>
                    <td className={cellClass()} data-label="Customer" data-primary><Link to={`/customers/${row.customerId}`} className="text-blue-700 hover:underline break-words">{row.customerName}</Link></td>
                    <td className={cellClass(true)} data-label="Invoices">{row.invoiceCount}</td>
                    <td className={cellClass(true)} data-label="Net revenue">{reportMoney(row.revenue)}</td>
                    <td className={cellClass(true)} data-label="Discounts">{reportMoney(row.discounts ?? '0')}</td>
                    <td className={cellClass(true)} data-label="Tax">{reportMoney(row.tax)}</td>
                  </tr>
                ))}
              </ReportTable>
            </ReportCard>
          </>
        )}
      </ReportStatus>
    </ReportPage>
  );
}
